import { DISCS_PER_FORMAT } from './types';
import type { DiscFormat, Movie, Watch } from './types';

export interface MonthBucket {
  /** `YYYY-MM`, in local time. */
  month: string;
  watches: number;
  minutes: number;
}

export interface TopMovie {
  movieId: string;
  movie: Movie | null;
  watches: number;
  minutes: number;
}

export interface GenreBucket {
  genreId: number;
  /** Movies on the shelf carrying this genre. */
  movies: number;
  /** Watches of movies carrying this genre. */
  watches: number;
}

export interface DecadeBucket {
  /** e.g. 1990 for the nineties. */
  decade: number;
  movies: number;
}

export interface Stats {
  movieCount: number;
  /** Cases on the shelf. A movie owned twice counts twice. */
  copyCount: number;
  /** Physical discs. A combo case counts as two. */
  discCount: number;
  /** Copies per format — a combo counts once, under `combo`. */
  formatCounts: Record<DiscFormat, number>;
  /** Movies available in each format — a combo counts under `4k` *and* `hd`. */
  formatAvailability: Record<DiscFormat, number>;

  watchCount: number;
  watchedMovieCount: number;
  unwatchedMovieCount: number;
  /** 0–1. Share of the shelf that has been played at least once. */
  watchedShare: number;

  totalMinutes: number;
  watchesThisYear: number;
  minutesThisYear: number;

  averageRating: number | null;
  /** Index 0 = one star … index 4 = five stars. */
  ratingCounts: [number, number, number, number, number];
  ratedCount: number;

  topMovies: TopMovie[];
  genres: GenreBucket[];
  decades: DecadeBucket[];
  perMonth: MonthBucket[];
  /** The busiest month in `perMonth`, or null when nothing was watched. */
  busiestMonth: MonthBucket | null;

  firstWatchAt: string | null;
  lastWatchAt: string | null;
  loanedCopyCount: number;
}

export interface StatsOptions {
  now?: Date;
  /** How many months the per-month series covers, including the current one. */
  months?: number;
  /** How many entries `topMovies` returns. */
  topCount?: number;
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Runtime to credit for a watch: the snapshot first, the movie as a fallback. */
export function watchMinutes(watch: Watch, movie: Movie | undefined): number {
  return watch.runtimeMinutes ?? movie?.runtime ?? 0;
}

export function computeStats(movies: Movie[], watches: Watch[], options: StatsOptions = {}): Stats {
  const now = options.now ?? new Date();
  const monthSpan = options.months ?? 12;
  const topCount = options.topCount ?? 10;

  const byId = new Map(movies.map((movie) => [movie.id, movie]));
  const thisYear = now.getFullYear();

  const formatCounts: Record<DiscFormat, number> = { '4k': 0, hd: 0, combo: 0 };
  const formatAvailability: Record<DiscFormat, number> = { '4k': 0, hd: 0, combo: 0 };
  const genreMovies = new Map<number, number>();
  const genreWatches = new Map<number, number>();
  const decadeCounts = new Map<number, number>();

  let copyCount = 0;
  let discs = 0;
  let loanedCopyCount = 0;

  for (const movie of movies) {
    copyCount += movie.copies.length;
    let has4k = false;
    let hasHd = false;
    let hasCombo = false;

    for (const copy of movie.copies) {
      formatCounts[copy.format] += 1;
      discs += DISCS_PER_FORMAT[copy.format];
      if (copy.loan) loanedCopyCount += 1;
      if (copy.format === '4k' || copy.format === 'combo') has4k = true;
      if (copy.format === 'hd' || copy.format === 'combo') hasHd = true;
      if (copy.format === 'combo') hasCombo = true;
    }

    if (has4k) formatAvailability['4k'] += 1;
    if (hasHd) formatAvailability.hd += 1;
    if (hasCombo) formatAvailability.combo += 1;

    for (const genreId of new Set(movie.genreIds)) {
      genreMovies.set(genreId, (genreMovies.get(genreId) ?? 0) + 1);
    }

    if (movie.year !== null) {
      const decade = Math.floor(movie.year / 10) * 10;
      decadeCounts.set(decade, (decadeCounts.get(decade) ?? 0) + 1);
    }
  }

  // Month buckets are pre-seeded so gaps render as zero rather than vanishing.
  const buckets = new Map<string, MonthBucket>();
  for (let offset = monthSpan - 1; offset >= 0; offset -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    buckets.set(monthKey(date), { month: monthKey(date), watches: 0, minutes: 0 });
  }

  const perMovie = new Map<string, { watches: number; minutes: number }>();
  const ratingCounts: [number, number, number, number, number] = [0, 0, 0, 0, 0];

  let totalMinutes = 0;
  let watchesThisYear = 0;
  let minutesThisYear = 0;
  let ratingSum = 0;
  let ratedCount = 0;
  let firstWatchAt: string | null = null;
  let lastWatchAt: string | null = null;

  for (const watch of watches) {
    const movie = byId.get(watch.movieId);
    const minutes = watchMinutes(watch, movie);
    const watchedAt = new Date(watch.watchedAt);

    totalMinutes += minutes;

    if (watchedAt.getFullYear() === thisYear) {
      watchesThisYear += 1;
      minutesThisYear += minutes;
    }

    const bucket = buckets.get(monthKey(watchedAt));
    if (bucket) {
      bucket.watches += 1;
      bucket.minutes += minutes;
    }

    const tally = perMovie.get(watch.movieId) ?? { watches: 0, minutes: 0 };
    tally.watches += 1;
    tally.minutes += minutes;
    perMovie.set(watch.movieId, tally);

    if (typeof watch.rating === 'number' && watch.rating >= 1 && watch.rating <= 5) {
      const index = (Math.round(watch.rating) - 1) as 0 | 1 | 2 | 3 | 4;
      ratingCounts[index] += 1;
      ratingSum += watch.rating;
      ratedCount += 1;
    }

    if (movie) {
      for (const genreId of new Set(movie.genreIds)) {
        genreWatches.set(genreId, (genreWatches.get(genreId) ?? 0) + 1);
      }
    }

    if (firstWatchAt === null || watch.watchedAt < firstWatchAt) firstWatchAt = watch.watchedAt;
    if (lastWatchAt === null || watch.watchedAt > lastWatchAt) lastWatchAt = watch.watchedAt;
  }

  const topMovies: TopMovie[] = [...perMovie.entries()]
    .map(([movieId, tally]) => ({
      movieId,
      movie: byId.get(movieId) ?? null,
      watches: tally.watches,
      minutes: tally.minutes,
    }))
    .sort((a, b) => b.watches - a.watches || b.minutes - a.minutes)
    .slice(0, topCount);

  const genres: GenreBucket[] = [...new Set([...genreMovies.keys(), ...genreWatches.keys()])]
    .map((genreId) => ({
      genreId,
      movies: genreMovies.get(genreId) ?? 0,
      watches: genreWatches.get(genreId) ?? 0,
    }))
    .sort((a, b) => b.movies - a.movies || b.watches - a.watches);

  const decades: DecadeBucket[] = [...decadeCounts.entries()]
    .map(([decade, count]) => ({ decade, movies: count }))
    .sort((a, b) => a.decade - b.decade);

  const perMonth = [...buckets.values()];
  const busiestMonth = perMonth.reduce<MonthBucket | null>(
    (best, bucket) =>
      bucket.watches > 0 && (!best || bucket.watches > best.watches) ? bucket : best,
    null,
  );

  const watchedMovieCount = movies.filter((movie) => movie.watchCount > 0).length;

  return {
    movieCount: movies.length,
    copyCount,
    discCount: discs,
    formatCounts,
    formatAvailability,

    watchCount: watches.length,
    watchedMovieCount,
    unwatchedMovieCount: movies.length - watchedMovieCount,
    watchedShare: movies.length === 0 ? 0 : watchedMovieCount / movies.length,

    totalMinutes,
    watchesThisYear,
    minutesThisYear,

    averageRating: ratedCount === 0 ? null : ratingSum / ratedCount,
    ratingCounts,
    ratedCount,

    topMovies,
    genres,
    decades,
    perMonth,
    busiestMonth,

    firstWatchAt,
    lastWatchAt,
    loanedCopyCount,
  };
}

/** Recompute the denormalised watch fields a movie document carries. */
export function watchSummary(watches: Watch[]): {
  watchCount: number;
  lastWatchedAt: string | null;
} {
  let lastWatchedAt: string | null = null;
  for (const watch of watches) {
    if (lastWatchedAt === null || watch.watchedAt > lastWatchedAt) lastWatchedAt = watch.watchedAt;
  }
  return { watchCount: watches.length, lastWatchedAt };
}
